// Libs
import { Box, Grid2, InputLabel, Stack, Typography } from '@mui/material';
import classNames from 'classnames/bind';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
// Components, Layouts, Pages
import { BaseFilters, CommonDatePicker, Loading } from '~/components/common';
import { ColumnChart } from '~/components/module/report';
import ChildrenPage from '~/pages/common/childrenPage/ChildrenPage';
// Others
import { varColor } from '~/themes/themeVariables';
import { getReportSaleByCity } from '~/thunks/report/reportThunk';
import { BACK_TO_PREVIOUS_PAGE, EMPTY_STRING, saleByCityLegend } from '~/utils/constants/common';
import { DateFormatEnum } from '~/utils/enum/common';
import { formatCurrency } from '~/utils/helper/common';
import { IFormatDataReportRes, IQueryParamReport, IReportTechnicianCommon } from '~/utils/interface/report';
import { useAppDispatch } from '~/utils/redux/hooks';
// Styles, images, icons
import styles from './ReportSaleByCity.module.scss';

type Props = {};

const cx = classNames.bind(styles);

const defaultFilterVal: IQueryParamReport = {
  startTime: moment().startOf('month').format(DateFormatEnum.YYYY_MM_DD),
  endTime: moment().endOf('month').format(DateFormatEnum.YYYY_MM_DD),
};

const ReportSaleByCity = (props: Props) => {
  //#region Destructuring Props

  //#endregion Destructuring Props

  //#region Declare Hook
  const dispatch = useAppDispatch();
  const { t } = useTranslation(['admin/report', 'common/base']);
  const navigate = useNavigate();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [paramObject, setParamObject] = useState<IQueryParamReport>(defaultFilterVal);
  const [filterValues, setFilterValues] = useState<IQueryParamReport>(defaultFilterVal);

  const [reportSaleByCity, setReportSaleByCity] = useState<IReportTechnicianCommon>({ chart: [] });

  const [isLoading, setIsLoading] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  const preparedChartSaleByCity = useCallback(
    (data: IFormatDataReportRes): IReportTechnicianCommon => {
      if (!data?.chart) return { chart: [], categories: [] };

      const chartFormatted = data.chart.map((item) => ({
        name: t(saleByCityLegend[item.chartType]),
        data: item.chartData,
      }));

      return { ...data, chart: chartFormatted };
    },
    [t]
  );

  const handleGetSaleByCity = useCallback(
    (params: IQueryParamReport) => {
      setIsLoading(true);

      dispatch(getReportSaleByCity(params))
        .unwrap()
        .then((resp) => {
          if (!resp) return;

          const data = preparedChartSaleByCity(resp);
          setReportSaleByCity(data);
        })
        .catch((error) => {})
        .finally(() => {
          setIsLoading(false);
        });
    },
    [dispatch, preparedChartSaleByCity]
  );

  //#region Implement Hook
  useEffect(() => {
    if (!paramObject?.startTime || !paramObject?.endTime) return;

    handleGetSaleByCity(paramObject);
  }, [paramObject, handleGetSaleByCity]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleBack = () => {
    navigate(BACK_TO_PREVIOUS_PAGE);
  };

  const handleApplyFilter = (values: IQueryParamReport | undefined) => {
    if (!values) {
      setFilterValues(defaultFilterVal);
      return;
    }

    setFilterValues({ ...filterValues, ...values });

    if (values?.showBy || values?.startTime || values?.endTime) {
      setParamObject({ ...paramObject, ...values });
    }
  };
  //#endregion Handle Function

  return (
    <ChildrenPage
      id='reportTechnician'
      title={t('report_sale_by_city_page_title')}
      onBackToPreviousPage={handleBack}
      childrenToolbar={
        <>
          <BaseFilters value={filterValues} defaultValue={defaultFilterVal} onApply={handleApplyFilter}>
            {({ value, onChange }) => (
              <Stack gap={'12px'}>
                <Typography className={cx('title')}>
                  {t('common_action_filter_by', { ns: 'common/base' })}
                </Typography>

                <Box component={'div'} className={cx('filterGroup')}>
                  <InputLabel className={cx('label')}>
                    {t('report_sale_by_city_filter_custom_date')}
                  </InputLabel>
                  <CommonDatePicker
                    mode='rangeDate'
                    value={{
                      startDate: value?.startTime || EMPTY_STRING,
                      endDate: value?.endTime || EMPTY_STRING,
                    }}
                    width={240}
                    onChange={({ startDate, endDate }) => {
                      onChange && onChange({ name: 'startTime', value: startDate });
                      onChange && onChange({ name: 'endTime', value: endDate });
                    }}
                  />
                </Box>
              </Stack>
            )}
          </BaseFilters>
        </>
      }
    >
      <>
        <Grid2 container size={12} rowSpacing={'20px'} columnSpacing={'20px'}>
          <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 6 }}>
            <ColumnChart
              title={t('chart_sale_by_city_title')}
              data={reportSaleByCity}
              categories={reportSaleByCity?.categories ?? []}
              colors={[varColor.backgroundMenuItemIconActive]}
              yAxisFormatter={(val) => formatCurrency(Number(val))}
            />
          </Grid2>
        </Grid2>

        {isLoading && <Loading />}
      </>
    </ChildrenPage>
  );
};

export default ReportSaleByCity;
